/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { logger } from "@/lib/utils/logger";
import { SignIn, SignUp } from "@clerk/nextjs";
import { AlertCircle, ArrowLeft } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useRef, useState } from "react";

function tabFromMode(mode: string | null): "login" | "signup" {
  if (mode === "signin" || mode === "login") return "login";
  if (mode === "signup") return "signup";
  return "signup";
}

function AuthPageContent() {
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState<"login" | "signup">(() =>
    tabFromMode(searchParams.get("mode")),
  );
  const [authError, setAuthError] = useState<string | null>(null);
  const clerkContainerRef = useRef<HTMLDivElement>(null);

  // Handle Enter key to submit Clerk forms (workaround for Clerk not handling Enter natively)
  // Listen at document level because Clerk components may render in shadow DOM
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;

      const target = event.target as HTMLElement;
      // Only handle if we're in an input field
      if (target.tagName !== "INPUT") return;

      // Check if the input is within our auth page (not some other part of the app)
      const container = clerkContainerRef.current;
      if (!container) return;

      // For Clerk's shadow DOM, check if the active element is an input
      const activeElement = document.activeElement;
      if (activeElement?.tagName !== "INPUT") return;

      // Find the Clerk submit button - check both container and document
      // Clerk may render buttons outside the container in some cases
      let submitButton = container.querySelector(
        'button[type="submit"], button.cl-formButtonPrimary, button[data-localization-key*="continue"], button[data-localization-key*="signIn"], button[data-localization-key*="signUp"]',
      ) as HTMLButtonElement | null;

      // If not found in container, try document-wide (for Clerk's portal rendering)
      if (!submitButton) {
        submitButton = document.querySelector(
          '.cl-rootBox button[type="submit"], .cl-rootBox button.cl-formButtonPrimary, .cl-card button[type="submit"], .cl-card button.cl-formButtonPrimary',
        ) as HTMLButtonElement | null;
      }

      if (submitButton && !submitButton.disabled) {
        event.preventDefault();
        submitButton.click();
      }
    };

    // Listen at document level to capture events from shadow DOM
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  // Keep tab in sync with URL (`?mode=signin` etc.); do not block first paint on a
  // "mounted" flag — that left Playwright and fast clients stuck on the loading shell.
  useEffect(() => {
    setActiveTab(tabFromMode(searchParams.get("mode")));
    setAuthError(null);
  }, [searchParams]);

  // Listen for Clerk errors via global error handlers
  useEffect(() => {
    const handleClerkError = (event: ErrorEvent) => {
      const errorMessage = event.message || String(event.error);

      // Check if this is a Clerk-related error
      if (
        errorMessage.includes("sign in attempt") ||
        errorMessage.includes("Server Action") ||
        errorMessage.includes("400") ||
        errorMessage.includes("factor")
      ) {
        logger.warn("[Auth] Clerk error detected", {
          error: errorMessage,
          tab: activeTab,
        });

        // Map Clerk errors to user-friendly messages
        if (
          errorMessage.includes("sign in attempt") ||
          errorMessage.includes("No sign in attempt")
        ) {
          setAuthError(
            "Your sign-in session has expired. Please try signing in again.",
          );
        } else if (errorMessage.includes("Server Action")) {
          // Server action errors are usually from hot reloading - suggest refresh
          setAuthError(
            "A temporary error occurred. Please refresh the page and try again.",
          );
        } else if (errorMessage.includes("400")) {
          setAuthError(
            "Invalid sign-in attempt. Please start over and try again.",
          );
        } else {
          setAuthError("An error occurred during sign-in. Please try again.");
        }
      }
    };

    // Listen for unhandled errors
    window.addEventListener("error", handleClerkError);

    // Also listen for unhandled promise rejections (Clerk uses promises)
    const handleRejection = (event: PromiseRejectionEvent) => {
      const errorMessage = event.reason?.message || String(event.reason);
      if (
        errorMessage.includes("sign in attempt") ||
        errorMessage.includes("Server Action") ||
        errorMessage.includes("400") ||
        errorMessage.includes("factor")
      ) {
        handleClerkError({
          message: errorMessage,
          error: event.reason,
        } as ErrorEvent);
      }
    };

    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleClerkError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, [activeTab]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-neutral-100/20 px-4 py-4">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <div className="mb-3">
          <Link
            className="inline-flex items-center text-sm text-muted-foreground transition-colors hover:text-secondary-600"
            href="/"
          >
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Back to Home
          </Link>
        </div>

        {/* Logo and Title */}
        <div className="mb-4 text-center">
          <div className="mb-3 flex items-center justify-center space-x-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
              <span className="text-sm font-bold text-white">CC</span>
            </div>
            <span className="text-base font-bold text-foreground">
              Contractor Co-Pilot
            </span>
          </div>
          <h1 className="mb-1 text-lg font-bold text-foreground">
            {activeTab === "login" ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {activeTab === "login"
              ? "Sign in to your account to continue"
              : "Get started — estimates, proposals, projects, and AI in one platform"}
          </p>
        </div>

        {/* Auth Card */}
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          {/* Tab Navigation */}
          <div className="mb-4 flex rounded-lg bg-muted p-1">
            <button
              className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-all ${
                activeTab === "login"
                  ? "bg-white text-secondary-600 shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setActiveTab("login")}
            >
              Sign In
            </button>
            <button
              className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-all ${
                activeTab === "signup"
                  ? "bg-white text-secondary-600 shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setActiveTab("signup")}
            >
              Sign Up
            </button>
          </div>

          {/* Error Display */}
          {authError && (
            <div className="mb-4 rounded-lg border border-destructive bg-destructive/10 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive">
                    Sign-in Error
                  </p>
                  <p className="mt-1 text-sm text-destructive">{authError}</p>
                  <button
                    onClick={() => {
                      setAuthError(null);
                      // Force a page refresh to clear any stale Clerk state
                      window.location.reload();
                    }}
                    className="mt-2 text-sm text-destructive underline hover:text-destructive/80"
                  >
                    Refresh and try again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Clerk Components */}
          <div ref={clerkContainerRef} className="min-h-[320px]">
            {activeTab === "login" ? (
              <SignIn
                appearance={{
                  elements: {
                    formButtonPrimary: "",
                    card: "shadow-none",
                    headerTitle: "hidden",
                    headerSubtitle: "hidden",
                    // Hide Clerk's default error messages since we're showing our own
                    alertText: "hidden",
                  },
                }}
                fallbackRedirectUrl="/post-auth"
                routing="path"
                path="/auth"
              />
            ) : (
              <SignUp
                appearance={{
                  elements: {
                    formButtonPrimary: "",
                    card: "shadow-none",
                    headerTitle: "hidden",
                    headerSubtitle: "hidden",
                    // Hide Clerk's default error messages since we're showing our own
                    alertText: "hidden",
                  },
                }}
                fallbackRedirectUrl="/post-auth"
                routing="path"
                path="/auth"
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 text-center text-sm text-muted-foreground">
          <p>
            By continuing, you agree to our{" "}
            <Link
              className="text-secondary-600 transition-colors hover:underline"
              href="/terms"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              className="text-secondary-600 transition-colors hover:underline"
              href="/privacy"
            >
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function AuthPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <AuthPageContent />
    </Suspense>
  );
}
